import { useEffect, useState } from "react";
import { ArticleService } from "../../services/articleServic";
import { toast } from "react-toastify";
import { parseList } from "../../utils/parseFields";

const inputBase =
  "w-full h-10 px-3 rounded-md border border-[#d3cccc] text-sm outline-none focus:border-[#521785] focus:ring-1 focus:ring-[#521785]";

const readOnlyInput = `${inputBase} bg-gray-50 text-gray-600`;

const Field = ({ label, required, error, children }) => (
  <div className="min-w-0">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-600"> *</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export const ModalShell = ({
  title,
  onClose,
  maxWidth = "max-w-lg",
  children,
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4"
    onClick={onClose}
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`w-full ${maxWidth} max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 z-10 flex items-center justify-between px-5 h-14 border-b border-[#d3cccc] bg-white">
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-gray-500 hover:text-gray-800 text-xl leading-none"
        >
          &times;
        </button>
      </div>
      {children}
    </div>
  </div>
);

const EMPTY_FORM = {
  journalId: "",
  journalName: "",
  link: "",
  title: "",
  abstract: "",
  authors: [],
  description: "",
  username: "",
  password: "",
};

const AddJournalModal = ({
  isOpen,
  onClose,
  onSave,
  mode = "add",
  documentId,
  projectId,
  articleId,
}) => {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(EMPTY_FORM);

  const [authorInput, setAuthorInput] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
      if (mode !== "edit") {
        return;
      }
      if (!articleId) {
        toast.error("Article ID is required.");
        return;
      }

      const fetchJournal = async () => {
        try {
          const response = await ArticleService.getProjectJournalArticlesByArticleID(articleId);
  
          if (response) {
            const article = response?.data?.data ?? response?.data ?? response;

            // const authors = Array.isArray(article?.authorName)
            // ? article.authorName
            // : article?.authorName
            //     ? [article.authorName]
            //     : [];

            const authors = parseList(article?.authorName);
            
            setForm({
              ...EMPTY_FORM,
              journalId: article?.journalId ?? "",
              journalName: article?.journal?.journalName ?? "",
              link: article?.journal?.link ?? "",
              title: article?.journal?.document?.title ?? "",
              abstract: article?.journal?.document?.abstract ?? "",
              authors,
              description: article?.description ?? "",
              username: article?.username ?? "",
              password: article?.password ?? "",
            });
        }
        } catch (error) {
          toast.error(error?.message || "Failed to fetch journal article.");
        }
      };
  
      fetchJournal();
    }, [articleId, mode]);

  useEffect(() => {
      if (!documentId) {
        toast.error("Document ID is required.");
        setJournals([]);
        return;
      }
  
      const fetchJournals = async () => {
        try {
          const response = await ArticleService.getJournals(    
            documentId,
            projectId,
          );
  
          if (response) {
            setJournals(response?.data || []);
          }
        } catch (error) {
          toast.error(error?.message || "Failed to fetch journal articles.");
        }
      };
  
      fetchJournals();
    }, [
      documentId,
      projectId,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleJournalChange = (e) => {
    const selectedId = Number(e.target.value);

    const selected = journals.find(
      (item) => item.id === selectedId
    );

    setForm((prev) => ({
      ...prev,
      journalId: selected?.id || "",
      journalName: selected?.journalName || "",
      link: selected?.link || "",
      title: selected?.documentTitle || "",
      abstract: selected?.documentAbstract || "",
    }));

    setErrors((prev) => ({
      ...prev,
      journalName: "",
    }));
  };

  const addAuthor = () => {
    const author = authorInput.trim();

    if (!author) return;

    const alreadyExists = form.authors.some(
      (item) => item.toLowerCase() === author.toLowerCase()
    );

    if (alreadyExists) {
      setAuthorInput("");
      return;
    }

    setForm((prev) => ({
      ...prev,
      authors: [...prev.authors, author],
    }));

    setAuthorInput("");
  };

  const removeAuthor = (authorToRemove) => {
    setForm((prev) => ({
      ...prev,
      authors: prev.authors.filter((author) => author !== authorToRemove),
    }));
  };

  const validate = () => {
    const next = {};

    if (!form.journalId) next.journalId = "Select a journal name";

    if (!form.journalName) next.journalName = "Select a journal name";

    if (!form.username.trim()) {
      next.username = "Enter a username";
    }

    if (!form.password) {
      next.password = "Enter a password";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async(e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      const payload={
        journalId:form.journalId,
        authorName: form.authors,
        username:form.username.trim(),
        password:form.password.trim(),
        description: form.description ? form.description.trim() : null,
      }

      const res = articleId
                  ? await ArticleService.updateProjectJournalArticlesByArticleID(articleId,payload)
                  : await ArticleService.addJournalsArticles(payload);
      
      if (res?.success === false) {
        throw new Error(res?.message || 
          (isEdit
            ? "Failed to update journal article."
            : "Failed to add journal article.")
        );
      }
      
      toast.success(res?.message || 
        (isEdit
          ? "Journal article updated successfully."
          : "Journal article added successfully.")
      );
      
      setForm({
        ...EMPTY_FORM
      });
      onClose?.();
      
      onSave?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        (isEdit
          ? "Failed to update journal article."
          : "Failed to add journal article.")
      );
    } finally {
      setLoading(false);
    }
  };

  if(!isOpen){
    return null;
  }

  const SectionTitle = ({ children, hint }) => (
    <div className="md:col-span-2 border-b border-gray-200 pb-2 mt-2 first:mt-0">
      <h3 className="text-sm font-semibold text-[#521785]">{children}</h3>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );

return (
  <ModalShell
    title={isEdit ? "Update journal" : "Add journal"}
    onClose={onClose}
    maxWidth="max-w-3xl"
  >
    <form onSubmit={handleSubmit} className="flex flex-col">
      <div className="max-h-[calc(100vh-14rem)] overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
        <SectionTitle hint="Select a journal and its details are filled in automatically.">
          Journal details
        </SectionTitle>

        <div className="md:col-span-2">
          <Field label="Journal name" required error={errors.journalName}>
            <select
              name="journalId"
              value={form.journalId || ""}
              onChange={handleJournalChange}
              disabled={isEdit}
              className={`${inputBase} ${
                isEdit ? "bg-gray-50 cursor-not-allowed" : "bg-white"
              }`}
            >
              <option value="">Select journal name</option>
              {journals.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.journalName} ({item.journalCode})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Title">
          <input
            value={form.title}
            readOnly
            placeholder="Automatically populated"
            className={readOnlyInput}
          />
        </Field>

        <Field label="Journal link">
          {form.link ? (
            <a
              href={form.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center h-10 px-3 rounded-md border border-gray-200 bg-gray-50 text-sm text-blue-700 underline truncate"
              title={form.link}
            >
              {form.link}
            </a>
          ) : (
            <input
              readOnly
              placeholder="Select a journal to display its link"
              className={readOnlyInput}
            />
          )}
        </Field>

        <div className="md:col-span-2">
          <Field label="Abstract">
            <textarea
              value={form.abstract}
              readOnly
              rows={2}
              placeholder="Automatically populated when you select a journal"
              className={`${readOnlyInput} h-auto py-2 resize-y max-h-48`}
            />
          </Field>
        </div>

        <SectionTitle>Authors</SectionTitle>

        <div className="md:col-span-2">
          <div className="flex gap-2">
            <input
              value={authorInput}
              onChange={(e) => setAuthorInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addAuthor();
                }
              }}
              placeholder="Enter author name and press Enter"
              className={inputBase}
            />
            <button
              type="button"
              onClick={addAuthor}
              className="h-10 shrink-0 px-4 rounded-md bg-[#521785] text-white text-sm hover:bg-[#421168]"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3 min-h-[2rem]">
            {form.authors.length > 0 ? (
              form.authors.map((author, index) => (
                <span
                  key={`${author}-${index}`}
                  className="inline-flex items-center gap-2 rounded-full bg-purple-50 border border-purple-200 pl-3 pr-2 py-1 text-xs text-[#521785]"
                >
                  {author}
                  <button
                    type="button"
                    onClick={() => removeAuthor(author)}
                    aria-label={`Remove ${author}`}
                    className="h-4 w-4 flex items-center justify-center rounded-full hover:bg-red-100 hover:text-red-600"
                  >
                    &times;
                  </button>
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-400">No authors added</span>
            )}
          </div>
        </div>

        

        <SectionTitle >
          Access credentials
        </SectionTitle>

        <Field label="Username" required error={errors.username}>
          <input
            name="username"
            value={form.username}
            onChange={handleChange}
            autoComplete="off"
            className={inputBase}
          />
        </Field>

        <Field label="Password" required error={errors.password}>
          <div className="relative">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              className={`${inputBase} pr-16`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-800"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </Field>

        <SectionTitle>Additional info</SectionTitle>

        <div className="md:col-span-2">
          <Field label="Description (Optional)">
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              placeholder="Enter journal description"
              className={`${inputBase} h-auto py-2 resize-y`}
            />
          </Field>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="flex justify-end gap-3 px-5 h-16 items-center border-t border-[#d3cccc] bg-white rounded-b-lg">
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="h-9 px-4 rounded-md border border-[#d3cccc] text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="h-9 px-4 rounded-md bg-[#521785] text-white text-sm hover:bg-[#421168] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading
            ? isEdit
              ? "Updating..."
              : "Saving..."
            : isEdit
              ? "Update journal"
              : "Save journal"}
        </button>
      </div>
    </form>
  </ModalShell>
);

};

export default AddJournalModal;